import React, { useState, useEffect } from 'react';
import PageHeader from 'Components/PageHeader';
import { useTranslation } from 'react-i18next';
import { shouldShowMerchants } from 'Selectors';
import RulePerformanceReportView from 'Routes/Reports/components/RulePerformanceReportView';
import MerchantSummaryReportView from 'Routes/Reports/components/MerchantSummaryReportView';
import appStyles from 'Routes/Routes.css';
import useGetLatestRepositoryConfig from 'Hooks/query-hooks/repository/useGetLatestRepositoryConfig';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import RouteLoading from 'Components/RouteLoading';
import _ from 'lodash';
import useCubeReportQuery from 'Hooks/query-hooks/reports/useCubeReportQuery';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import { Loading } from '@brighterion/br-ui';
import { Query } from '@cubejs-client/core';
import { getCubeReportQuery } from './components/NewReportForm/helpers';
import NewReportForm from './components/NewReportForm';
import styles from './index.css';
import SaveReport from './components/SaveReport';
import ExportCSV from './components/ExportCSV';
import ReportNotSavedWarning from './components/ReportNotSavedWarning';

type Props = {
    reportType: ReportType;
};

const NewReport = ({ reportType }: Props) => {
    const [extendedFormValues, setFormValues] = useState<ReportDataCount | undefined>(undefined);
    const [saveBtnClicked, setSaveBtnClicked] = useState<boolean>(false);
    const [countQuery, setCountQuery] = useState<Query>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [, setError] = useState<string>();
    const { t } = useTranslation();
    const { data: repositoryData } = useGetLatestRepositoryConfig();
    const { fetchAccessToken } = useAccessTokenStore.getState();
    const { users } = useGetReduxUsers();
    const { resultSet } = useCubeReportQuery(extendedFormValues, countQuery);
    const reportCountData = resultSet?.tablePivot() || [];
    const reportTypeTitle =
        shouldShowMerchants() || reportType === 'performance'
            ? t('newReportPage', { reportType })
            : t('newTransactionReportPage');

    const handleSubmitForm = async (values: ReportDataCount) => {
        try {
            setIsLoading(true);
            await fetchAccessToken();
            setFormValues(values);
        } catch (e) {
            // TODO: display the error message on a toast. error state not used
            setError('Error submitting form');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (reportType === 'performance' && extendedFormValues) {
            const query = getCubeReportQuery(extendedFormValues);
            setCountQuery(query);
        }
    }, [extendedFormValues, reportType]);

    const renderReport = () => {
        switch (reportType) {
            case 'merchantsummary': {
                return <MerchantSummaryReportView newlyGenerated formValues={extendedFormValues} users={users} />;
            }
            case 'performance': {
                return (
                    <RulePerformanceReportView
                        newlyGenerated
                        formValues={extendedFormValues}
                        users={users}
                        reportCountData={reportCountData}
                    />
                );
            }
            default: {
                return false;
            }
        }
    };

    if (_.isUndefined(repositoryData)) {
        return <RouteLoading />;
    }

    return (
        <div className={appStyles.landingPage}>
            <PageHeader>
                <PageHeader.Left className={styles.titleText}>{reportTypeTitle}</PageHeader.Left>
                <PageHeader.Right>
                    <ExportCSV
                        extendedFormValues={extendedFormValues}
                        setSaveBtnClicked={setSaveBtnClicked}
                        reportType={reportType}
                    />
                    <span className={styles.saveReportButton}>
                        <SaveReport
                            extendedFormValues={extendedFormValues}
                            setSaveBtnClicked={setSaveBtnClicked}
                            reportType={reportType}
                        />
                    </span>
                </PageHeader.Right>
            </PageHeader>

            <NewReportForm setFormValues={handleSubmitForm} config={repositoryData!} reportType={reportType} />

            {isLoading ? (
                <div data-testid='loading-container' className={styles.loadingContainer}>
                    <Loading size='lg' />
                </div>
            ) : (
                renderReport()
            )}

            {!saveBtnClicked && (
                <ReportNotSavedWarning
                    extendedFormValues={extendedFormValues}
                    saveBtnClicked={saveBtnClicked}
                    reportType={reportType}
                />
            )}
        </div>
    );
};

export default NewReport;
